import { useEffect, useRef, useState } from "react";
import { Map as MLMap, Marker } from "maplibre-gl";
import type { Device } from "../../lib/supabase";
import { MAP_STYLE, applyBasemap, type Basemap } from "../../lib/basemap";
import { ago, kmh, liveSpeed, statusOf, STATUS_LABEL } from "../../lib/format";
import { iconSvg } from "../../lib/icons";
import { markerParts, setSilhouette } from "../../lib/vehicleMarker";
import { stoppedLabel, useStoppedFor } from "../../lib/stopped";

/**
 * Floating, draggable, resizable window that keeps one object centred.
 * Several can be open at once (one per object).
 */
export default function FollowWindow({ device, index, basemap, now, onClose, onFocus }: {
  device: Device; index: number; basemap: Basemap; now: number; onClose: () => void; onFocus: () => void;
}) {
  const [pos, setPos] = useState(() => ({ x: Math.max(16, window.innerWidth - 360 - index * 28), y: 70 + index * 28 }));
  const [z, setZ] = useState(20 + index);
  const box = useRef<HTMLDivElement>(null);
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<MLMap | null>(null);
  const mk = useRef<Marker | null>(null);
  const [zoomed, setZoomed] = useState(15);
  const s = device.state;

  useEffect(() => {
    if (!el.current) return;
    const c: [number, number] = s?.lat != null ? [s.lng!, s.lat] : [178, -17.8];
    const m = new MLMap({ container: el.current, style: MAP_STYLE, center: c, zoom: 15, attributionControl: false, interactive: true });
    m.on("zoomend", () => setZoomed(m.getZoom()));
    map.current = m;
    const ro = new ResizeObserver(() => m.resize());
    ro.observe(el.current);
    return () => { ro.disconnect(); m.remove(); map.current = null; mk.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => { if (map.current) applyBasemap(map.current, basemap); }, [basemap]);

  useEffect(() => {
    const m = map.current;
    if (!m || s?.lat == null || s.lng == null) return;
    if (!mk.current) {
      const e = document.createElement("div");
      e.className = "mk";
      mk.current = new Marker({ element: e, anchor: "center" }).setLngLat([s.lng, s.lat]).addTo(m);
    }
    const st = statusOf(s, now);
    const e = mk.current.getElement();
    e.dataset.status = st;
    e.dataset.custom = device.icon_color ? "true" : "false";
    if (device.icon_color) e.style.setProperty("--mk-bg", device.icon_color); else e.style.removeProperty("--mk-bg");
    setSilhouette(markerParts(e, false).dot, device.icon, s.course, s.speed_kn == null ? null : Number(s.speed_kn) * 1.852);
    mk.current.setLngLat([s.lng, s.lat]);
    m.easeTo({ center: [s.lng, s.lat], zoom: zoomed, duration: 800 });
  }, [s?.lat, s?.lng, s?.course, now]); // eslint-disable-line

  function startDrag(ev: React.PointerEvent) {
    if ((ev.target as HTMLElement).closest("button")) return;
    const sx = ev.clientX - pos.x, sy = ev.clientY - pos.y;
    const move = (e: PointerEvent) => setPos({
      x: Math.min(Math.max(0, e.clientX - sx), window.innerWidth - 120),
      y: Math.min(Math.max(52, e.clientY - sy), window.innerHeight - 60),
    });
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  const st = statusOf(s, now);
  const stoppedFor = stoppedLabel(useStoppedFor(device.id, s, now), now);   // V3.9.2
  return (
    <div ref={box} className="follow-win" style={{ left: pos.x, top: pos.y, zIndex: z }} onPointerDown={() => setZ(Date.now() % 100000 + 100)}>
      <header onPointerDown={startDrag}>
        <span className={`licon ${st}`} dangerouslySetInnerHTML={{ __html: iconSvg(device.icon, 13) }} />
        <b title={device.name}>{device.name}</b>
        <span className="mono sub">{liveSpeed(s, st)}</span>
        <button className="x" onClick={onFocus} title="Show on main map" aria-label="Show on main map">⌖</button>
        <button className="x" onClick={onClose} aria-label="Close">×</button>
      </header>
      <div className="follow-map" ref={el} />
      <footer><span className={`pill ${st}`}>{STATUS_LABEL[st]}{stoppedFor && <span className="pill-time"> · {stoppedFor}</span>}</span><span className="sub">updated {ago(s?.seen_at, now)}</span></footer>
    </div>
  );
}
